/**
 * ConnectionsTree Component (Story 32.1 — Epic 32 Multi-Account UX)
 *
 * The management surface of the client Integrations tab: per platform, a
 * tree of OAuth grants → attached resources, surfacing the Epic 31 two-axis
 * model (which login a property came from; which axis is broken when
 * something fails). Renders ABOVE the platform tile grid — the grid stays
 * the "connect a new platform" affordance.
 *
 * Accessibility: each grant is collapsible via the WAI-ARIA DISCLOSURE
 * pattern (real <button> + aria-expanded + aria-controls), NOT role="tree" —
 * W3C APG guidance: full treeview semantics are wrong for styled lists.
 *
 * Status badges are driven EXCLUSIVELY by the server's `statusBadge` field
 * (the exhaustive derivation lives in services/client-connections.ts) — the
 * client never re-derives status from raw syncStatus.
 */

import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "motion/react";
import {
  CaretDown,
  CaretRight,
  CircleNotch,
  Plugs,
  Plus,
  Prohibit,
  Trash,
} from "@phosphor-icons/react";
import { ADAPTER_META, OAUTH_FAMILIES } from "@provenleap/integrations";
import { apiGet } from "../../lib/apiClient";
import { PICKER_PLATFORMS, logoUrl } from "../../lib/integrations";
import type { ToastType } from "../../hooks/useToast";
import {
  ONE_CLICK_OAUTH_PLATFORMS,
  SECONDARY_PROMPT_OAUTH_PLATFORMS,
} from "../../hooks/useIntegrationConnect";
import { RevokeGrantDialog, type RevokeGrantTarget } from "./RevokeGrantDialog";
import {
  AddAnotherGrantDialog,
  type AddAnotherGrantTarget,
} from "./AddAnotherGrantDialog";
import type { DiscardIncompleteSetupTarget } from "./DiscardIncompleteSetupDialog";

// ============================================================================
// Read-model types (mirror apps/server/src/services/client-connections.ts)
// ============================================================================

export type StatusBadge =
  | "active"
  | "unavailable"
  | "auth_failed"
  | "sync_error"
  | "pending"
  | "syncing"
  | "paused";

export interface ConnectionResource {
  integrationId: string;
  grantResourceId: string | null;
  externalAccountId: string | null;
  externalAccountName: string | null;
  resourceStatus: "active" | "unavailable" | null;
  syncStatus: string;
  statusBadge: StatusBadge;
  lastSyncAt: string | null;
  /** Server-proven exact legacy placeholder; the mutation re-proves it. */
  canDiscardIncompleteSetup?: boolean;
}

export interface ConnectionGrant {
  oauthTokenId: string | null;
  provider: string | null;
  providerAccountName: string | null;
  tokenStatus: string | null;
  grantCreatedAt: string | null;
  isDemo: boolean;
  tenantImpact: { clientCount: number; integrationCount: number };
  resources: ConnectionResource[];
}

export interface ConnectionPlatform {
  platform: string;
  displayName: string;
  oauthGrantScope: "multi-tenant" | "single-tenant";
  authKind: "oauth" | "api-key";
  grants: ConnectionGrant[];
}

interface ConnectionsResponse {
  data: { platforms: ConnectionPlatform[] };
}

// ============================================================================
// Display helpers
// ============================================================================

/** DOM id of a platform section — the tile grid scrolls here (AC #10). */
export function connectionsPlatformSectionId(platform: string): string {
  return `connections-platform-${platform}`;
}

const PLATFORM_DOMAIN = new Map(PICKER_PLATFORMS.map((p) => [p.platform, p.domain]));

/** "microsoft-ads" → "Microsoft Ads" (derived — never a per-provider switch). */
function familyLabel(family: string): string {
  return family
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/** Token statuses that show the red "Needs re-authorization" pill (AC #11). */
const DEGRADED_TOKEN_STATUSES = new Set(["refresh_failed", "revoked"]);

const BADGE_META: Record<StatusBadge, { label: string; className: string; spin?: boolean }> = {
  active: {
    label: "Active",
    className: "bg-green-50 text-green-800 border border-green-200",
  },
  unavailable: {
    label: "Unavailable",
    className: "bg-yellow-50 text-yellow-800 border border-yellow-200",
  },
  auth_failed: {
    label: "Auth failed",
    className: "bg-red-50 text-red-800 border border-red-200",
  },
  sync_error: {
    label: "Sync error",
    className: "bg-yellow-50 text-yellow-800 border border-yellow-200",
  },
  pending: {
    label: "Pending",
    className: "bg-muted text-muted-foreground border border-input",
  },
  syncing: {
    label: "Syncing",
    className: "bg-muted text-muted-foreground border border-input",
    spin: true,
  },
  paused: {
    label: "Paused",
    className: "bg-muted text-muted-foreground border border-input",
  },
};

function StatusBadgePill({ badge }: { badge: StatusBadge }) {
  const meta = BADGE_META[badge] ?? BADGE_META.sync_error;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${meta.className}`}
    >
      {meta.spin && <CircleNotch size={12} className="animate-spin" />}
      {meta.label}
    </span>
  );
}

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(iso));
}

function formatConnectedDate(iso: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(iso));
}

/** Lead label of a grant header (AC #11 — the email IS the answer). */
function grantTitle(grant: ConnectionGrant): string {
  if (grant.isDemo) return "Demo data";
  if (grant.providerAccountName) return grant.providerAccountName;
  if (grant.grantCreatedAt) return `Connected ${formatConnectedDate(grant.grantCreatedAt)}`;
  return "Connected account";
}

/** Secondary label: "Google account", "Mailchimp account", "API key", … */
function grantKindLabel(grant: ConnectionGrant, authKind: ConnectionPlatform["authKind"]): string {
  if (grant.isDemo) return "No live connection";
  if (authKind === "api-key") return "API key";
  if (grant.provider) return `${familyLabel(grant.provider)} account`;
  return "Connection";
}

/**
 * True when the grant's family has NO declarative revocationEndpoint — the
 * revoke is local-only (dialog adds the "also revoke at the provider" line).
 * Derived from OAUTH_FAMILIES, never a hardcoded provider list.
 */
function isLocalOnlyRevoke(provider: string | null): boolean {
  if (!provider) return true;
  const family = (OAUTH_FAMILIES as Record<string, { revocationEndpoint?: unknown }>)[provider];
  return !family?.revocationEndpoint;
}

/**
 * Story 32.2 — whether a platform group offers "Add another grant".
 * Three derived conditions, never a hardcoded platform list:
 * 1. server-derived `authKind === "oauth"` (API_KEY_PLATFORMS membership —
 *    api-key platforms have no OAuth-grant concept even though several
 *    declare an `oauthFamily` for token-lifecycle purposes);
 * 2. `ADAPTER_META[platform].oauthFamily` defined (AC #1b derivation rule);
 * 3. the one-click `connect()` dispatch can actually launch the family
 *    (ONE_CLICK_OAUTH_PLATFORMS — derived from the same package arrays the
 *    dispatch uses; legacy picker-modal-only families auto-appear here the
 *    day they join the dispatch).
 */
function canOfferAddGrant(platformGroup: ConnectionPlatform): boolean {
  if (platformGroup.authKind !== "oauth") return false;
  const meta = (
    ADAPTER_META as Record<string, { oauthFamily?: string } | undefined>
  )[platformGroup.platform];
  if (!meta?.oauthFamily) return false;
  return ONE_CLICK_OAUTH_PLATFORMS.has(platformGroup.platform);
}

/** Story 32.2 — the provider/family display name for the dialog copy. */
function addGrantProviderName(platform: string): string {
  const meta = (
    ADAPTER_META as Record<string, { oauthFamily?: string } | undefined>
  )[platform];
  return meta?.oauthFamily ? familyLabel(meta.oauthFamily) : "the provider";
}

// ============================================================================
// Component
// ============================================================================

export interface DisconnectResourceTarget {
  id: string;
  platform: string;
  accountName: string | null;
}

interface ConnectionsTreeProps {
  clientId: string;
  /** owner/admin — gates Disconnect + Revoke affordances (AC #13). */
  canManage: boolean;
  showToast: (type: ToastType, message: string) => void;
  /**
   * Opens the EXISTING DisconnectIntegrationDialog in IntegrationsCard —
   * the whole `handleDisconnected` chain (notification dismissal, cache
   * invalidation, google last-connection keep/revoke) keeps working.
   */
  onDisconnectResource: (target: DisconnectResourceTarget) => void;
  /**
   * Story 32.2 — launches `connect(platform, { forceNewGrant: true })` in
   * IntegrationsCard. When absent the "Add another grant" affordance hides
   * (e.g. legacy mounts/tests that haven't wired the connect hook).
   */
  onAddGrant?: (platform: string) => void;
  /** Opens the narrow local-only legacy placeholder removal dialog. */
  onDiscardIncompleteSetup?: (
    target: DiscardIncompleteSetupTarget,
  ) => void;
  /** Story 32.2 — the hook's in-flight platform, for the dialog pending guard. */
  connectingPlatform?: string | null;
}

export function ConnectionsTree({
  clientId,
  canManage,
  showToast,
  onDisconnectResource,
  onAddGrant,
  onDiscardIncompleteSetup,
  connectingPlatform = null,
}: ConnectionsTreeProps) {
  const { data } = useQuery<ConnectionPlatform[]>({
    queryKey: ["client-connections", clientId],
    queryFn: async () => {
      const res = await apiGet(`/api/clients/${clientId}/connections`);
      if (!res.ok) return [];
      const json = (await res.json()) as ConnectionsResponse;
      return json.data.platforms;
    },
  });

  // Disclosure state — default EXPANDED; we track the collapsed set.
  const [collapsedGrants, setCollapsedGrants] = useState<Set<string>>(new Set());

  // Revoke dialog — always-mounted with the lingering lastValue pattern
  // (§4.6: the dialog needs a non-null grant prop through its exit animation).
  const [revokeTarget, setRevokeTarget] = useState<RevokeGrantTarget | null>(null);
  const [lastRevokeTarget, setLastRevokeTarget] = useState<RevokeGrantTarget | null>(null);
  useEffect(() => {
    if (revokeTarget) {
      setLastRevokeTarget(revokeTarget);
      return;
    }
    if (!lastRevokeTarget) return;
    const t = setTimeout(() => setLastRevokeTarget(null), 300);
    return () => clearTimeout(t);
  }, [revokeTarget, lastRevokeTarget]);

  // Story 32.2 — Add-another-grant dialog, same §4.6 lastValue pattern.
  const [addGrantTarget, setAddGrantTarget] = useState<AddAnotherGrantTarget | null>(null);
  const [lastAddGrantTarget, setLastAddGrantTarget] =
    useState<AddAnotherGrantTarget | null>(null);
  useEffect(() => {
    if (addGrantTarget) {
      setLastAddGrantTarget(addGrantTarget);
      return;
    }
    if (!lastAddGrantTarget) return;
    const t = setTimeout(() => setLastAddGrantTarget(null), 300);
    return () => clearTimeout(t);
  }, [addGrantTarget, lastAddGrantTarget]);

  const platforms = useMemo(() => data ?? [], [data]);

  if (platforms.length === 0) return null;

  function toggleGrant(grantKey: string) {
    setCollapsedGrants((prev) => {
      const next = new Set(prev);
      if (next.has(grantKey)) next.delete(grantKey);
      else next.add(grantKey);
      return next;
    });
  }

  return (
    <div className="mb-8" data-testid="connections-tree">
      <h2 className="text-[15px] font-semibold text-foreground mb-3">Connections</h2>

      <div className="space-y-4">
        {platforms.map((platformGroup) => {
          const domain = PLATFORM_DOMAIN.get(platformGroup.platform);
          const platformHasDiscardableIncompleteSetup =
            platformGroup.grants.some((grant) =>
              grant.resources.some(
                (resource) => resource.canDiscardIncompleteSetup === true,
              ),
            );
          return (
            <section
              key={platformGroup.platform}
              id={connectionsPlatformSectionId(platformGroup.platform)}
              tabIndex={-1}
              aria-label={`${platformGroup.displayName} connections`}
              className="rounded-[22px] bg-muted/50 shadow-xs outline-none scroll-mt-24"
              style={{ border: "0.5px solid var(--hairline)" }}
            >
              {/* Platform section header */}
              <div className="flex items-center gap-2.5 px-5 pt-4 pb-2">
                {domain && (
                  <img
                    src={logoUrl(domain)}
                    alt=""
                    width={18}
                    height={18}
                    className="shrink-0 rounded-sm"
                    loading="lazy"
                  />
                )}
                <h3 className="text-[13.5px] font-semibold text-foreground">
                  {platformGroup.displayName}
                </h3>

                {/* Story 32.2 — "Add another grant" (owner/admin, OAuth-family
                    platforms only; visible whether or not a real grant exists) */}
                {canManage &&
                  onAddGrant &&
                  !platformHasDiscardableIncompleteSetup &&
                  canOfferAddGrant(platformGroup) && (
                  <button
                    type="button"
                    aria-label={`Add another ${platformGroup.displayName} grant`}
                    // Review F2: same lock as the tile grid (`!connectingPlatform`)
                    // — while ANY platform's auth-url fetch is in flight, no new
                    // connect may start (a second runRedirect would race the
                    // first for window.location).
                    disabled={connectingPlatform !== null}
                    onClick={() =>
                      setAddGrantTarget({
                        platform: platformGroup.platform,
                        displayName: platformGroup.displayName,
                        providerName: addGrantProviderName(platformGroup.platform),
                        hasExistingGrant: platformGroup.grants.some(
                          (g) => g.oauthTokenId !== null && !g.isDemo
                        ),
                      })
                    }
                    className="ml-auto shrink-0 inline-flex items-center gap-1 rounded-2xl px-2.5 py-1 text-[12px] text-foreground shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all bg-background disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-xs"
                    style={{ border: "0.5px solid var(--hairline)" }}
                  >
                    <Plus size={14} />
                    Add another grant
                  </button>
                )}
              </div>

              <div className="px-3 pb-3 space-y-1.5">
                {platformGroup.grants.map((grant) => {
                  // Unique per platform: token ids are unique; the demo and
                  // keyless pseudo-grants each appear at most once. Keeping
                  // the key index-free keeps collapse state stable across
                  // refetches that reorder grants.
                  const grantKey = `${platformGroup.platform}:${grant.oauthTokenId ?? (grant.isDemo ? "__demo__" : "__keyless__")}`;
                  const panelId = `grant-panel-${grantKey.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
                  const isExpanded = !collapsedGrants.has(grantKey);
                  const isDegraded =
                    grant.tokenStatus !== null && DEGRADED_TOKEN_STATUSES.has(grant.tokenStatus);
                  const hasDiscardableIncompleteSetup = grant.resources.some(
                    (resource) =>
                      resource.canDiscardIncompleteSetup === true,
                  );
                  const canRevoke =
                    canManage &&
                    grant.oauthTokenId !== null &&
                    !grant.isDemo &&
                    !hasDiscardableIncompleteSetup;

                  return (
                    <div
                      key={grantKey}
                      className="rounded-2xl bg-background shadow-xs"
                      style={{ border: "0.5px solid hsl(var(--input) / 0.6)" }}
                    >
                      {/* Grant header — disclosure button + sibling action */}
                      <div className="flex items-center gap-2 pl-2 pr-3 py-2">
                        <button
                          type="button"
                          aria-expanded={isExpanded}
                          // Only reference the panel while it exists —
                          // AnimatePresence unmounts it when collapsed, and
                          // aria-controls must not point at a missing id
                          // (review F3).
                          aria-controls={isExpanded ? panelId : undefined}
                          onClick={() => toggleGrant(grantKey)}
                          className="flex flex-1 min-w-0 items-center gap-2 rounded-xl px-2 py-1.5 text-left cursor-pointer hover:bg-muted/30 transition-colors"
                        >
                          {isExpanded ? (
                            <CaretDown size={16} className="shrink-0 text-muted-foreground" />
                          ) : (
                            <CaretRight size={16} className="shrink-0 text-muted-foreground" />
                          )}
                          <span className="min-w-0 flex items-center gap-2 flex-wrap">
                            <span className="text-[13px] font-medium text-foreground truncate">
                              {grantTitle(grant)}
                            </span>
                            <span className="text-[11px] text-muted-foreground shrink-0">
                              {grantKindLabel(grant, platformGroup.authKind)}
                            </span>
                            {isDegraded && (
                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-50 text-red-800 border border-red-200 shrink-0">
                                Needs re-authorization
                              </span>
                            )}
                            <span className="text-[11px] text-muted-foreground shrink-0">
                              {grant.resources.length}{" "}
                              {grant.resources.length === 1 ? "resource" : "resources"}
                            </span>
                          </span>
                        </button>

                        {canRevoke && (
                          <button
                            type="button"
                            onClick={() =>
                              setRevokeTarget({
                                oauthTokenId: grant.oauthTokenId!,
                                // Platform-qualified for api-key grants so the
                                // dialog copy reads "…the Google PageSpeed API
                                // key…" (review R2 copy fix).
                                providerLabel:
                                  platformGroup.authKind === "api-key"
                                    ? `${platformGroup.displayName} API key`
                                    : grantKindLabel(grant, platformGroup.authKind),
                                isApiKey: platformGroup.authKind === "api-key",
                                localOnly: isLocalOnlyRevoke(grant.provider),
                                clientCount: grant.tenantImpact.clientCount,
                                integrationCount: grant.tenantImpact.integrationCount,
                              })
                            }
                            className="shrink-0 inline-flex items-center gap-1 rounded-2xl px-2.5 py-1 text-[12px] text-destructive shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all bg-background"
                            style={{ border: "0.5px solid var(--hairline)" }}
                          >
                            <Prohibit size={14} />
                            {platformGroup.authKind === "api-key"
                              ? "Remove API key"
                              : "Revoke OAuth grant"}
                          </button>
                        )}
                      </div>

                      {/* Resources panel — disclosure content (0.2s easeInOut §6) */}
                      <AnimatePresence initial={false}>
                        {isExpanded && (
                          <motion.div
                            id={panelId}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <ul className="px-3 pb-2 space-y-0.5">
                              {grant.resources.map((resource) => {
                                const isDiscardableIncompleteSetup =
                                  resource.canDiscardIncompleteSetup === true;
                                const displayName =
                                  resource.externalAccountName ??
                                  resource.externalAccountId ??
                                  "Pending setup";
                                return (
                                  <li
                                    key={resource.integrationId}
                                    className="flex items-center gap-2.5 rounded-xl px-3 py-2 hover:bg-muted/20 transition-colors"
                                  >
                                    <Plugs size={14} className="shrink-0 text-muted-foreground" />
                                    <span className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
                                      <span className="text-[13px] text-foreground truncate">
                                        {displayName}
                                      </span>
                                      {resource.externalAccountId &&
                                        resource.externalAccountName && (
                                          <span className="text-[11px] text-muted-foreground truncate">
                                            {resource.externalAccountId}
                                          </span>
                                        )}
                                      <StatusBadgePill badge={resource.statusBadge} />
                                      {resource.lastSyncAt && (
                                        <span className="text-[11px] text-muted-foreground shrink-0">
                                          synced {formatRelative(resource.lastSyncAt)}
                                        </span>
                                      )}
                                    </span>
                                    {canManage &&
                                      isDiscardableIncompleteSetup &&
                                      onDiscardIncompleteSetup &&
                                      grant.oauthTokenId !== null &&
                                      resource.grantResourceId !== null && (
                                        <button
                                          type="button"
                                          aria-label="Remove incomplete setup"
                                          onClick={() =>
                                            onDiscardIncompleteSetup({
                                              integrationId:
                                                resource.integrationId,
                                              clientId,
                                              platform:
                                                platformGroup.platform,
                                              displayName:
                                                platformGroup.displayName,
                                              oauthTokenId:
                                                grant.oauthTokenId!,
                                              oauthGrantResourceId:
                                                resource.grantResourceId!,
                                            })
                                          }
                                          className="shrink-0 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] text-destructive shadow-xs cursor-pointer hover:bg-background transition-colors"
                                          style={{
                                            border:
                                              "0.5px solid var(--hairline)",
                                          }}
                                        >
                                          <Trash size={14} />
                                          Remove incomplete setup
                                        </button>
                                      )}
                                    {canManage &&
                                      !isDiscardableIncompleteSetup && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          onDisconnectResource({
                                            id: resource.integrationId,
                                            platform: platformGroup.platform,
                                            accountName: resource.externalAccountName,
                                          })
                                        }
                                        className="shrink-0 text-[12px] text-muted-foreground hover:text-destructive cursor-pointer px-2 py-1 rounded-lg transition-colors"
                                      >
                                        Disconnect
                                      </button>
                                      )}
                                  </li>
                                );
                              })}
                            </ul>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      {/* Always-mounted (§4.6) — lastRevokeTarget lingers through the exit */}
      {lastRevokeTarget && (
        <RevokeGrantDialog
          open={!!revokeTarget}
          onOpenChange={(open) => {
            if (!open) setRevokeTarget(null);
          }}
          grant={lastRevokeTarget}
          clientId={clientId}
          showToast={showToast}
        />
      )}

      {/* Story 32.2 — same §4.6 lastValue mounting. On Continue the page
          full-redirects to the provider, so the dialog never needs to close
          itself on success; errors reset connectingPlatform and unblock it. */}
      {lastAddGrantTarget && (
        <AddAnotherGrantDialog
          open={!!addGrantTarget}
          onOpenChange={(open) => {
            if (!open) setAddGrantTarget(null);
          }}
          target={lastAddGrantTarget}
          isPending={
            connectingPlatform !== null &&
            connectingPlatform === lastAddGrantTarget.platform
          }
          // Gate on the LIVE target, not the lingering lastValue: during the
          // ~250ms exit animation of a CANCELLED dialog the Continue button is
          // still mounted — a click there must be a no-op, never an OAuth
          // launch (review R2). Also no-op while ANY platform is mid-connect
          // (review F2 defense-in-depth behind the disabled affordance).
          onConfirm={() => {
            if (!addGrantTarget || connectingPlatform !== null) return;
            onAddGrant?.(addGrantTarget.platform);
            // Review F1: Shopify (secondary-prompt family) hands off to the
            // subdomain dialog instead of fetching — connectingPlatform never
            // gets set on that path, so close this confirm dialog at handoff
            // rather than leaving two stacked modals.
            if (SECONDARY_PROMPT_OAUTH_PLATFORMS.has(addGrantTarget.platform)) {
              setAddGrantTarget(null);
            }
          }}
        />
      )}
    </div>
  );
}
